import { HttpBackend, HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, defer, map, Observable, of, retry, shareReplay, startWith, Subject, switchMap, throwError, timeout, TimeoutError, timer } from 'rxjs';

import { UPDATE_ANNOUNCEMENT_FEED_URL } from './update-announcement.config';
import { IUpdateAnnouncement, IUpdateAnnouncementFeed } from './update-announcement.interface';
import { UpdateAnnouncementFeedHelper } from './update-announcement-feed.helper';


@Injectable({ providedIn: 'root' })
export class UpdateAnnouncementFeedService {

	//** Configurations */
	private readonly REQUEST_TIMEOUT_MS: number = 8000;

	//** Feed Data */
	readonly feed$: Observable<IUpdateAnnouncementFeed>;
	readonly updates$: Observable<IUpdateAnnouncement[]>;
	private readonly httpClient: HttpClient;
	private readonly refreshRequests: Subject<void> = new Subject<void>();
	private visitStarted: boolean = false;


	constructor(backend: HttpBackend) {
		// A dedicated client keeps the app's authentication and encryption headers off the feed request.
		this.httpClient = new HttpClient(backend);
		this.feed$ = this.refreshRequests.pipe(
			startWith(null),
			switchMap(() => this.createFeed().pipe(startWith(UpdateAnnouncementFeedHelper.parseDocument(null)))),
			shareReplay({ bufferSize: 1, refCount: false })
		);
		this.updates$ = this.feed$.pipe(map((feed) => feed.updates));
	}


	/**------------------------------------------------------
	 * Load Shared Feed
	 */
	startVisit(): void {
		// The navbar may already have started the first request. Later logins need a fresh feed.
		if (this.visitStarted) this.refreshRequests.next();
		this.visitStarted = true;
	}

	private createFeed(): Observable<IUpdateAnnouncementFeed> {
		// A fresh query avoids a cached S3 document without requiring CORS preflight headers.
		return defer(() => (UPDATE_ANNOUNCEMENT_FEED_URL ? this.httpClient.get<unknown>(UPDATE_ANNOUNCEMENT_FEED_URL, { params: { updateCheck: Date.now() } }) : of([]))).pipe(
			timeout(this.REQUEST_TIMEOUT_MS),
			retry({
				count: 2,
				delay: (error: unknown, retryCount: number) => {
					const temporary: boolean = error instanceof TimeoutError || (error instanceof HttpErrorResponse && (error.status === 0 || error.status === 408 || error.status === 429 || error.status >= 500));
					return temporary ? timer(1000 * retryCount) : throwError(() => error);
				}
			}),
			map((content: unknown) => UpdateAnnouncementFeedHelper.parseDocument(content)),
			catchError(() => of(UpdateAnnouncementFeedHelper.parseDocument(null)))
		);
	}
}
